/**
 * Nekromant — Schwarzer Rettich ("Nekrettich"). LMAO-Style (BATTLE-14/17):
 * fast-schwarze MATTE Knolle, bucklig-kauernde Silhouette (Caster-Familie,
 * docs/13 §3), subtiler Violett-Schimmer als Rim (#9a6dd7 = Keyword "Schatten"),
 * unten laeuft eine duenne WEISSE Wurzelspitze aus + weisses Fleisch blitzt als
 * Sichel am unteren Rand hervor (Kontrast-Anker), oben zerzauste dunkle
 * Blattstummel wie eine zerrissene Kapuze.
 * 2★: eingeritzte Seelen-Glyphen (Narben) an den Flanken.
 * 3★: violette Seelen-Wisps umkreisen die Knolle (pulsieren mit castPulse).
 * Referenz-Qualitaet: kohlrabi.ts / okra.ts. Contract: species/types.ts.
 */
import type { PaintParams, SpeciesPainter } from './types'
import { darken, lighten, rgba, specular, volumeGradient } from '../draw3d'

/** Violett-Schimmer (Brief / docs/13 §6 "Schatten") — nur Rim/Glyphen/Wisps, nie Body-Fuellung */
const SOUL = '#9a6dd7'

export const paintRettich: SpeciesPainter = (p: PaintParams) => {
  const { ctx, x, y, r, colors, tier, pose } = p
  const dead = p.emotion === 'dead'
  // weisses Rettich-Fleisch aus body abgeleitet (dead => automatisch hellgrau)
  const flesh = lighten(colors.body, 76)
  const outlineW = Math.max(2, r * 0.12)

  ctx.save()
  ctx.lineJoin = 'round'
  ctx.translate(x, y)
  ctx.rotate(0.05) // kauernder Vorwaerts-Lean — er steht nie aufrecht

  // --- Knollen-Silhouette: Buckel-Kuppe links oben, Ruecken faellt rechts ab ---
  const bodyPath = (): void => {
    ctx.beginPath()
    ctx.moveTo(-r * 0.3, -r * 0.94)
    ctx.bezierCurveTo(r * 0.18, -r * 1.0, r * 0.68, -r * 0.72, r * 0.86, -r * 0.16)
    ctx.bezierCurveTo(r * 0.98, r * 0.3, r * 0.62, r * 0.9, r * 0.06, r * 0.97)
    ctx.bezierCurveTo(-r * 0.48, r * 0.94, -r * 0.92, r * 0.6, -r * 0.95, r * 0.02)
    ctx.bezierCurveTo(-r * 0.97, -r * 0.55, -r * 0.72, -r * 0.88, -r * 0.3, -r * 0.94)
    ctx.closePath()
  }

  // --- zerzauste Blattstummel (HINTER dem Korpus): zerrissene Kapuzen-Zacken ---
  const stump = (dx: number, baseY: number, ang: number, h: number, w: number): void => {
    ctx.save()
    ctx.translate(dx * r, baseY * r)
    ctx.rotate(ang)
    const g = ctx.createLinearGradient(-w * r, 0, w * r, 0)
    g.addColorStop(0, lighten(colors.body, 12))
    g.addColorStop(0.5, darken(colors.body, 6))
    g.addColorStop(1, colors.bodyDark)
    ctx.fillStyle = g
    ctx.strokeStyle = colors.outline
    ctx.lineWidth = Math.max(1.5, r * 0.06)
    ctx.beginPath()
    ctx.moveTo(-w * r, 0)
    ctx.lineTo(-w * r * 0.72, -h * r * 0.55)
    ctx.lineTo(-w * r * 0.2, -h * r * 0.42) // Riss-Kerbe
    ctx.lineTo(w * r * 0.02, -h * r) // zerfetzte Spitze
    ctx.lineTo(w * r * 0.42, -h * r * 0.48)
    ctx.lineTo(w * r * 0.78, -h * r * 0.6) // zweite Zacke
    ctx.lineTo(w * r, 0)
    ctx.closePath()
    ctx.fill()
    ctx.stroke()
    ctx.restore()
  }
  stump(-0.56, -0.68, -0.6, 0.34, 0.13)
  stump(-0.3, -0.85, -0.22, 0.5, 0.15)
  stump(-0.02, -0.9, 0.08, 0.4, 0.14)
  stump(0.27, -0.8, 0.34, 0.52, 0.15)
  stump(0.52, -0.62, 0.66, 0.3, 0.12)

  // --- duenne weisse Wurzelspitze (HINTER dem Korpus, laeuft unten aus) ---
  ctx.fillStyle = flesh
  ctx.strokeStyle = colors.outline
  ctx.lineWidth = Math.max(1.5, r * 0.055)
  ctx.beginPath()
  ctx.moveTo(-r * 0.26, r * 0.78)
  ctx.quadraticCurveTo(-r * 0.1, r * 1.14, r * 0.16, r * 1.28) // rechte Kante zur Spitze
  ctx.quadraticCurveTo(-r * 0.08, r * 1.12, -r * 0.02, r * 0.8) // linke Kante zurueck
  ctx.closePath()
  ctx.fill()
  ctx.stroke()

  // --- Korpus: matte, fast-schwarze Knolle mit Volumen ---
  ctx.fillStyle = volumeGradient(ctx, -r * 0.05, -r * 0.05, r, colors.body, 14, 26)
  ctx.strokeStyle = colors.outline
  ctx.lineWidth = outlineW
  bodyPath()
  ctx.fill()
  ctx.stroke()

  // --- Innenleben, geclippt auf den Korpus ---
  ctx.save()
  bodyPath()
  ctx.clip()

  // Kapuzen-Zone: zerrissener dunkler Schleier ueber Kuppe + Schultern
  const hood = ctx.createLinearGradient(0, -r * 1.0, 0, -r * 0.4)
  hood.addColorStop(0, rgba(colors.bodyDark, 0.95))
  hood.addColorStop(1, rgba(colors.bodyDark, 0.55))
  ctx.fillStyle = hood
  ctx.beginPath()
  ctx.moveTo(-r * 1.0, -r * 0.34)
  ctx.lineTo(-r * 0.76, -r * 0.54)
  ctx.lineTo(-r * 0.54, -r * 0.4)
  ctx.lineTo(-r * 0.32, -r * 0.64)
  ctx.lineTo(-r * 0.1, -r * 0.44)
  ctx.lineTo(r * 0.14, -r * 0.68)
  ctx.lineTo(r * 0.38, -r * 0.46)
  ctx.lineTo(r * 0.6, -r * 0.62)
  ctx.lineTo(r * 0.84, -r * 0.4)
  ctx.lineTo(r * 1.0, -r * 0.5)
  ctx.lineTo(r * 1.0, -r * 1.3)
  ctx.lineTo(-r * 1.0, -r * 1.3)
  ctx.closePath()
  ctx.fill()
  // Licht-Saum unter der Riss-Kante (Lichtquelle oben-links faengt die Fetzen)
  ctx.strokeStyle = rgba(lighten(colors.body, 24), dead ? 0.2 : 0.4)
  ctx.lineWidth = Math.max(1, r * 0.035)
  ctx.beginPath()
  ctx.moveTo(-r * 0.76, -r * 0.51)
  ctx.lineTo(-r * 0.54, -r * 0.37)
  ctx.lineTo(-r * 0.32, -r * 0.61)
  ctx.lineTo(-r * 0.1, -r * 0.41)
  ctx.lineTo(r * 0.14, -r * 0.65)
  ctx.lineTo(r * 0.38, -r * 0.43)
  ctx.stroke()

  // matte Skin-Striationen (Rettich-Furchen) an den Flanken, Gesicht-Zone bleibt ruhig
  ctx.lineCap = 'round'
  ctx.strokeStyle = rgba(darken(colors.body, 30), 0.8)
  ctx.lineWidth = Math.max(1.2, r * 0.05)
  for (const [sx, bend] of [[-0.72, -0.16], [0.62, 0.18], [0.82, 0.1]] as const) {
    ctx.beginPath()
    ctx.moveTo(sx * r, -r * 0.2)
    ctx.quadraticCurveTo((sx + bend) * r, r * 0.35, sx * r * 0.72, r * 0.78)
    ctx.stroke()
  }
  ctx.strokeStyle = rgba(lighten(colors.body, 18), dead ? 0.15 : 0.35)
  ctx.lineWidth = Math.max(1, r * 0.03)
  ctx.beginPath()
  ctx.moveTo(-r * 0.6, -r * 0.1)
  ctx.quadraticCurveTo(-r * 0.7, r * 0.3, -r * 0.5, r * 0.7)
  ctx.stroke()

  // weisses Fleisch blitzt als Sichel am unteren Rand hervor (starker Kontrast!)
  ctx.fillStyle = flesh
  ctx.beginPath()
  ctx.ellipse(-r * 0.02, r * 1.18, r * 0.66, r * 0.44, 0.06, 0, Math.PI * 2)
  ctx.fill()
  // Sichel-Oberkante: harte Schnitt-Linie (wirkt "geschaelt")
  ctx.strokeStyle = rgba(darken(colors.body, 32), 0.9)
  ctx.lineWidth = Math.max(1.2, r * 0.045)
  ctx.beginPath()
  ctx.ellipse(-r * 0.02, r * 1.18, r * 0.66, r * 0.44, 0.06, Math.PI * 1.15, Math.PI * 1.85)
  ctx.stroke()
  // zwei kurze Schab-Kratzer ueber der Sichel (geschnitzter Look)
  ctx.strokeStyle = rgba(flesh, 0.55)
  ctx.lineWidth = Math.max(1, r * 0.035)
  for (const [kx, ky, kl] of [[-0.3, 0.62, 0.16], [0.24, 0.66, 0.12]] as const) {
    ctx.beginPath()
    ctx.moveTo(kx * r, ky * r)
    ctx.lineTo((kx + 0.05) * r, (ky + kl) * r)
    ctx.stroke()
  }

  // 2★: eingeritzte Seelen-Glyphen (Narben) — dunkler Sockel + violetter Glimmer
  if (tier >= 2 && !dead) {
    const glyphA = 0.55 + pose.castPulse * 0.35
    const glyphs = (): void => {
      ctx.beginPath()
      // linke Flanke: Hakenkreuzung
      ctx.moveTo(-r * 0.58, r * 0.28)
      ctx.lineTo(-r * 0.44, r * 0.4)
      ctx.lineTo(-r * 0.56, r * 0.52)
      ctx.moveTo(-r * 0.52, r * 0.3)
      ctx.lineTo(-r * 0.4, r * 0.24)
      // rechte Flanke: Zickzack-Sigill
      ctx.moveTo(r * 0.42, r * 0.24)
      ctx.lineTo(r * 0.56, r * 0.34)
      ctx.lineTo(r * 0.44, r * 0.44)
      ctx.lineTo(r * 0.58, r * 0.54)
    }
    ctx.lineCap = 'round'
    ctx.strokeStyle = rgba(colors.outline, 0.9) // Ritz-Sockel
    ctx.lineWidth = Math.max(2, r * 0.08)
    glyphs()
    ctx.stroke()
    ctx.strokeStyle = rgba(SOUL, glyphA)
    ctx.lineWidth = Math.max(1, r * 0.035)
    ctx.shadowColor = SOUL
    ctx.shadowBlur = r * (0.08 + pose.castPulse * 0.14)
    glyphs()
    ctx.stroke()
    ctx.shadowBlur = 0
  }
  ctx.restore() // Clip Ende

  // Outline nach dem Innenleben knackig nachziehen (Silhouetten-Readability)
  ctx.strokeStyle = colors.outline
  ctx.lineWidth = outlineW
  bodyPath()
  ctx.stroke()

  // --- Licht: subtiler VIOLETT-Schimmer als Rim entlang der Buckel-Kante ---
  if (!dead) {
    const rimA = 0.38 + pose.castPulse * 0.3 + (tier >= 3 ? 0.08 : 0)
    ctx.save()
    ctx.lineCap = 'round'
    ctx.strokeStyle = rgba(SOUL, rimA)
    ctx.lineWidth = Math.max(1.5, r * 0.06)
    ctx.shadowColor = SOUL
    ctx.shadowBlur = r * (0.06 + pose.castPulse * 0.16)
    ctx.beginPath()
    ctx.moveTo(-r * 0.82, r * 0.26)
    ctx.bezierCurveTo(-r * 0.88, -r * 0.36, -r * 0.6, -r * 0.76, -r * 0.22, -r * 0.82)
    ctx.stroke()
    ctx.restore()
    // matte Knolle: nur ein gedaempfter, kleiner Glanzpunkt oben-links
    specular(ctx, -r * 0.34, -r * 0.42, r * 0.11, 0.16)
  }

  // 3★: violette Seelen-Wisps umkreisen die Knolle (heben sich beim Cast)
  if (tier >= 3 && !dead) {
    const lift = pose.castPulse * r * 0.12
    const wispA = 0.7 + pose.castPulse * 0.3
    const wisp = (wx: number, wy: number, s: number, bend: number): void => {
      ctx.save()
      ctx.translate(wx * r, wy * r - lift)
      ctx.fillStyle = rgba(SOUL, wispA)
      ctx.shadowColor = SOUL
      ctx.shadowBlur = s * 1.6
      ctx.beginPath()
      ctx.moveTo(bend * s, -s * 1.7) // Seelen-Flammen-Spitze, seitlich gebogen
      ctx.bezierCurveTo(s * 0.9, -s * 0.6, s * 0.85, s * 0.35, 0, s * 0.75)
      ctx.bezierCurveTo(-s * 0.85, s * 0.35, -s * 0.55, -s * 0.5, bend * s, -s * 1.7)
      ctx.closePath()
      ctx.fill()
      // heller Kern (Luminanz-Kontrast)
      ctx.shadowBlur = 0
      ctx.fillStyle = rgba(lighten(SOUL, 32), wispA)
      ctx.beginPath()
      ctx.ellipse(0, s * 0.18, s * 0.3, s * 0.46, 0, 0, Math.PI * 2)
      ctx.fill()
      ctx.restore()
    }
    wisp(-1.04, -0.42, r * 0.13, -0.5)
    wisp(1.0, -0.14, r * 0.11, 0.55)
    wisp(0.72, -1.02, r * 0.09, 0.35)
  }

  ctx.restore()
}
